import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, MapPin, Calendar, Palette, Award } from "lucide-react";
import { useLocale } from "@/context/locale-context";

export default function ArtistProfile() {
  const { t } = useLocale();

  const stats = [
    { value: t.artist?.worksCount       || "80+", label: t.artist?.works       || "Obras creadas"         },
    { value: t.artist?.collectionsCount || "25+", label: t.artist?.collections || "Colecciones privadas"  },
    { value: t.artist?.exhibitionsCount || "12",  label: t.artist?.exhibitions || "Exposiciones"          },
    { value: t.artist?.countriesCount   || "6",   label: t.artist?.countries   || "Países con obra"       },
  ];

  const details = [
    { icon: MapPin,   label: t.artist?.birthLabel  || "Nacimiento",  value: t.artist?.birth  || "Colombia, 1994"          },
    { icon: Calendar, label: t.artist?.careerLabel || "Trayectoria", value: t.artist?.career || "Más de 15 años"          },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="artist-profile-page">

      {/* ── Hero ───────────────────────────────────────────────── */}
      <section className="relative h-[40vh] md:h-[55vh] overflow-hidden bg-muted flex items-end">
        <div className="absolute inset-0">
          <img
            src="https://res.cloudinary.com/dpgqeudvk/image/upload/v1778122833/IMG-20190907-WA0013.jpg_1_q9j6xf.jpg"
            alt="Artista"
            className="w-full h-full object-cover object-top opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
        </div>
        <div className="relative z-10 container mx-auto px-4 md:px-8 pb-8 md:pb-14">
          <p className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-2">
            {t.artist?.eyebrow || "Perfil del artista"}
          </p>
          <h1 className="text-3xl sm:text-5xl md:text-6xl font-serif leading-none mb-2">
            {t.artist?.name || "Oscar Amir Camacho Cruz"}
          </h1>
          <p className="text-xs md:text-base text-muted-foreground max-w-lg">
            {t.artist?.subtitle || "Artista plastico · Colombia"}
          </p>
        </div>
      </section>

      {/* ── Stats strip ────────────────────────────────────────── */}
      <section className="border-y bg-card">
        <div className="container mx-auto px-4 md:px-8 py-4 md:py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:divide-x divide-border/50 text-center">
            {stats.map((s, i) => (
              <div key={i} className="px-2 md:px-4">
                <p className="text-xl md:text-3xl font-serif mb-0.5">{s.value}</p>
                <p className="text-[9px] md:text-[10px] uppercase tracking-widest text-muted-foreground leading-tight">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Foto + Bio ─────────────────────────────────────────── */}
      <section className="container mx-auto px-4 md:px-8 py-10 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-8 md:gap-16 items-start max-w-5xl mx-auto">

          {/* ── Foto: más pequeña en móvil ── */}
          <div className="relative mx-auto w-3/4 sm:w-2/3 lg:w-full">
            <div className="overflow-hidden bg-muted aspect-[3/4]">
              <img
                src="https://res.cloudinary.com/dpgqeudvk/image/upload/v1778122833/IMG-20190907-WA0013.jpg_1_q9j6xf.jpg"
                alt="Retrato del artista"
                className="w-full h-full object-cover object-top transition-transform duration-700 hover:scale-[1.02]"
              />
            </div>
            
          </div>

          {/* ── Bio ── */}
          <div className="flex flex-col justify-start">
            <div className="w-8 h-px bg-foreground mb-5" />

            <h2 className="text-xl md:text-3xl font-serif mb-4">
              {t.artist?.aboutTitle || "Sobre el artista"}
            </h2>

            {/* Párrafos: en móvil solo el primero visible, los demás colapsados */}
            <div className="space-y-3 text-muted-foreground text-sm leading-6 md:leading-7 mb-6">
              <p>{t.artist?.paragraph1 || "Soy Oscar Amir Camacho Cruz, colombiano, nacido en la cosmopolita Bogotá en 1994, criado en la ciudad de Medellín en el municipio de Bello; cuna del arte, el tango y la cultura paisa. Desde muy pequeño me sentí atraído por la pintura, el dibujo, el baile, la música, la escultura; solía vender dibujos a mis compañeros en 1ro de bachillerato."}</p>
              {/* Párrafos 2 y 3: ocultos en móvil, visibles en md+ */}
              <p className="hidden md:block">{t.artist?.paragraph2 || "Mis padres vieron cualidades artísticas prematuras y así fue como a los escasos 8 años tuve la oportunidad de ser aprendiz del gran maestro Rodrigo Carmona Montoya compañero del reconocido artista Pedro Nel Gómez, que me introdujo al mundo de los lienzos y el olor a óleo. A mis 14 años hice mi primer mural de 3 x 3 metros mientras cursaba bachillerato. En el año 2012 ingresé a la Universidad de San Buenaventura donde me titulé como Licenciado en arte y cultura. Participe como coordinador y promotor del arte y la cultura en la primera infancia en el Instituto Colombiano de Bienestar Familiar (ICBF) y como docente extracurricular en la Institución Educativa Fernando Vèlez (I.E. Fernando Vèlez)."}</p>
              <p className="hidden md:block">{t.artist?.paragraph3 || "Desde 2021 he sido parte de proyectos comunitarios dando vida a las calles y fachadas de Colombia como Pinta mi Islote en la isla Santa Cruz del Islote y Dejando Huellas a la Vista de America en el municipio de Cocorná. Hoy, los pinceles me han puesto en España y espero ampliar mi experiencia artística viviendo la cultura del flamenco, las fallas y el mar mediterráneo, integrando escenas, visiones y realidades que complementen este proyecto de vida. "}</p>
              
            </div>

            {/* Detalles: 2 columnas en móvil y desktop */}
            <div className="grid grid-cols-2 gap-2 md:gap-3 mb-6">
              {details.map(({ icon: Icon, label, value }, i) => (
                <div key={i} className="flex items-start gap-2 border p-2.5 md:p-3">
                  <Icon className="w-3.5 h-3.5 md:w-4 md:h-4 text-muted-foreground mt-0.5 shrink-0" />
                  <div>
                    <p className="text-[9px] md:text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">{label}</p>
                    <p className="text-xs md:text-sm font-medium leading-tight">{value}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Quote */}
            <blockquote className="pl-3 md:pl-4 border-l border-muted-foreground/30 text-xs md:text-sm italic text-muted-foreground/80 leading-6 mb-6">
              {t.artist?.quote || '“El pincel y los colores son los artistas, yo soy un espectador dispuesto a la creación”.'}
            </blockquote>

            {/* Botones */}
            <div className="flex flex-wrap gap-2 md:gap-3">
              <Link to="/gallery">
                <Button size="sm" className="rounded-none px-5 md:px-6 uppercase tracking-widest text-[10px] md:text-xs h-9 md:h-10">
                  {t.artist?.viewWorks || "Ver obras"}
                </Button>
              </Link>
              <Link to="/Contact">
                <Button variant="outline" size="sm" className="rounded-none px-5 md:px-6 uppercase tracking-widest text-[10px] md:text-xs h-9 md:h-10">
                  {t.artist?.contact || "Contacto"}
                  <ArrowRight className="ml-1.5 w-3 h-3" />
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* ── Párrafos 2 y 3: visibles en móvil debajo del grid ── */}
        <div className="md:hidden mt-8 max-w-5xl mx-auto space-y-3 text-muted-foreground text-sm leading-6">
          <p>{t.artist?.paragraph2 || "Mis padres vieron cualidades artísticas prematuras y así fue como a los escasos 8 años tuve la oportunidad de ser aprendiz del gran maestro Rodrigo Carmona Montoya compañero del reconocido artista Pedro Nel Gómez, que me introdujo al mundo de los lienzos y el olor a óleo. A mis 14 años hice mi primer mural de 3 x 3 metros mientras cursaba bachillerato. En el año 2012 ingresé a la Universidad de San Buenaventura donde me titulé como Licenciado en arte y cultura. Participe como coordinador y promotor del arte y la cultura en la primera infancia en el Instituto Colombiano de Bienestar Familiar (ICBF) y como docente extracurricular en la Institución Educativa Fernando Vèlez (I.E. Fernando Vèlez)."}</p>
          <p>{t.artist?.paragraph3 || "Desde 2021 he sido parte de proyectos comunitarios dando vida a las calles y fachadas de Colombia como Pinta mi Islote en la isla Santa Cruz del Islote y Dejando Huellas a la Vista de America en el municipio de Cocorná. Hoy, los pinceles me han puesto en España y espero ampliar mi experiencia artística viviendo la cultura del flamenco, las fallas y el mar mediterráneo, integrando escenas, visiones y realidades que complementen este proyecto de vida."}</p>
        </div>
      </section>

      {/* ── Hitos ──────────────────────────────────────────────── */}
      <section className="border-t bg-card">
        <div className="container mx-auto px-4 md:px-8 py-10 md:py-16 max-w-5xl">
          <h2 className="text-xl md:text-2xl font-serif mb-8 text-center">
            {t.artist?.milestonesTitle || "Hitos"}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {[
              { year: t.artist?.milestone1Year || "2005", desc: t.artist?.milestone1 || "Primera exposición individual"    },
              { year: t.artist?.milestone2Year || "2011", desc: t.artist?.milestone2 || "Reconocimiento nacional de artes" },
              { year: t.artist?.milestone3Year || "2017", desc: t.artist?.milestone3 || "Exposición en Europa"             },
              { year: t.artist?.milestone4Year || "2023", desc: t.artist?.milestone4 || "Colección internacional"          },
            ].map((m, i) => (
              <div key={i} className="relative pl-3 md:pl-4 border-l border-border">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{m.year}</p>
                <p className="text-xs md:text-sm font-medium leading-5">{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
}